import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService, GraphQLResponse } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';

import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    BuyerPaymentsGroupedDetailRequest,
    BuyerPaymentsGroupedDetailResponse
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/buyer-payments-list/get-grouped-payment';
import { MoneyDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/money-dto';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import { TransferDetailInput, TransferDetailResponse } from '../../model/models';

export class BuyerPaymentsGroupedDetailItemGF {
    batchKey?: string;
    groupId?: string;
    groupedChildSearchMatch?: boolean;
    groupedInvoiceKey?: string;
    invoiceAmount?: MoneyDto;
    invoiceDate?: string;
    invoiceKey?: string;
    invoiceNumber?: string;
    isGroup?: boolean;
    numberDaysPaidEarly?: number;
    paymentAmount?: MoneyDto;
    paymentDate?: string;
    paymentType?:
        | 'EARLY_PAYMENT'
        | 'EARLY_PAY_REMITTANCE'
        | 'INVOICE_TERM'
        | 'UNKNOWN_PAYMENT_TYPE'
        | 'PAY_LATER_PAYMENT';
    supplierKey?: string;
    supplierLegalName?: string;

    public constructor(init?: Partial<BuyerPaymentsGroupedDetailItemGF>) {
        Object.keys(init ?? {}).forEach((k) => (this[k] = init[k]));
    }
}

@Injectable({ providedIn: 'root' })
export class GetGroupedPaymentService extends ApiService<
    BuyerPaymentsGroupedDetailRequest,
    BuyerPaymentsGroupedDetailResponse
> {
    public requestType = BuyerPaymentsGroupedDetailRequest;
    public responseType = BuyerPaymentsGroupedDetailResponse;
    public endpointName = 'buyer/BuyerPaymentsList/GetGroupedPayment';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (isOn) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'buyer/BuyerPaymentsList/GetPayment';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: BuyerPaymentsGroupedDetailRequest
    ): { query: string } | BuyerPaymentsGroupedDetailRequest {
        if (this._isPayLaterEnabled) {
            const input: TransferDetailInput = {
                transferId: req.batchKey,
                payableId: req.invoiceKey.toString()
            };
            const query = jsonToGraphQLQuery({
                transferDetail: {
                    __args: { input },
                    rows: {
                        isGroup: true,
                        payableId: true,
                        payableName: true,
                        invoiceDate: true,
                        invoiceAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        paymentDate: true,
                        recipient: true,
                        supplier: true,
                        earlyPaymentDate: true,
                        onTermsPaymentDate: true,
                        transferType: true,
                        supplierKey: true,
                        approvals: {
                            amount: {
                                cents: true,
                                currencyCode: true
                            },
                            id: true,
                            status: true,
                            canBeRevoked: true,
                            adjustedDueDate: true,
                            statusLegacy: true,
                            invoice: {
                                amount: {
                                    cents: true,
                                    currencyCode: true
                                },
                                date: true,
                                terms: true,
                                invoiceNumber: true
                            }
                        },
                        creditMemos: {
                            amount: {
                                cents: true,
                                currencyCode: true
                            },
                            allocatedAmount: {
                                cents: true,
                                currencyCode: true
                            },
                            id: true,
                            date: true,
                            canBeRevoked: true,
                            creditMemoNumber: true
                        }
                    },
                    transferSummary: {
                        id: true,
                        transferType: true,
                        payerName: true,
                        recipientType: true,
                        invoiceCount: true
                    }
                }
            });
            return { query: `{${query}}` };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    public responseMapper(
        res: GraphQLResponse<TransferDetailResponse> | BuyerPaymentsGroupedDetailResponse
    ): BuyerPaymentsGroupedDetailResponse {
        if (this._isPayLaterEnabled) {
            const { transferDetail } = (res as GraphQLResponse<TransferDetailResponse>).data;
            const payable = transferDetail.rows[0];
            let paymentType = 'INVOICE_TERM';
            if (payable.onTermsPaymentDate) {
                paymentType = 'PAY_LATER_PAYMENT';
            }
            if (payable.earlyPaymentDate) {
                paymentType = 'EARLY_PAYMENT';
            }
            return new BuyerPaymentsGroupedDetailResponse({
                ok: true,
                rows: [
                    ...(payable.creditMemos?.map((creditMemo) => {
                        var creditMemoAllocatedAmount = creditMemo.allocatedAmount;
                        creditMemoAllocatedAmount.cents = -creditMemoAllocatedAmount.cents;

                        return new BuyerPaymentsGroupedDetailItemGF({
                            batchKey: transferDetail.transferSummary.id,
                            invoiceAmount: creditMemoAllocatedAmount,
                            groupId: payable.payableName,
                            groupedInvoiceKey: creditMemo.id,
                            invoiceDate: creditMemo.date,
                            invoiceNumber: creditMemo.creditMemoNumber,
                            invoiceKey: payable.payableId.toString(),
                            paymentDate: payable.paymentDate,
                            supplierKey: payable.supplierKey,
                            supplierLegalName: payable.supplier
                        });
                    }) ?? []),
                    ...payable.approvals?.map((approval) => {
                        return new BuyerPaymentsGroupedDetailItemGF({
                            batchKey: transferDetail.transferSummary.id,
                            invoiceAmount: approval.amount,
                            groupId: payable.payableName,
                            groupedInvoiceKey: approval.id,
                            invoiceDate: approval.invoice.date.toString(),
                            invoiceNumber: approval.invoice.invoiceNumber,
                            invoiceKey: payable.payableId.toString(),
                            paymentDate: payable.paymentDate,
                            supplierKey: payable.supplierKey,
                            supplierLegalName: payable.supplier
                        });
                    })
                ]
            } as unknown) as BuyerPaymentsGroupedDetailResponse;
        }
        return res as BuyerPaymentsGroupedDetailResponse;
    }
}
